import { useState, memo } from 'react';
import { styled, alpha } from '@mui/material/styles';
import { Box, Typography, IconButton } from '@mui/material';
import LocationOnOutlinedIcon from '@mui/icons-material/LocationOnOutlined';
import StarIcon from '@mui/icons-material/Star';
import ImageOutlinedIcon from '@mui/icons-material/ImageOutlined';
import ShareIcon from '@mui/icons-material/Share';
import type { VenueResponse } from '@lov/shared';
import { useVenueTypes, getVenueTypeLabel, getVenueTypeColor } from '../hooks/useVenueTypes';

interface VenueCardProps {
  venue: VenueResponse;
  onClick?: () => void;
}

// Styled Components
const CardContainer = styled(Box)(({ theme }) => ({
  backgroundColor: theme.palette.background.paper,
  borderRadius: 16,
  overflow: 'hidden',
  cursor: 'pointer',
  border: `1px solid ${theme.palette.divider}`,
  transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
  boxShadow: '0 4px 12px rgba(0, 0, 0, 0.05)',

  '&:hover': {
    transform: 'translateY(-4px)',
    boxShadow: '0 8px 24px rgba(0, 0, 0, 0.1)',
    borderColor: alpha(theme.palette.text.primary, 0.12),
  },
}));

const ImageSection = styled(Box)({
  position: 'relative',
  height: 200,
  backgroundColor: '#F5F5F5',
  overflow: 'hidden',
});

const CoverImage = styled('img')({
  width: '100%',
  height: '100%',
  objectFit: 'cover',
});

const ImagePlaceholder = styled(Box)(({ theme }) => ({
  width: '100%',
  height: '100%',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  color: theme.palette.text.disabled,
}));

const VenueTypeBadge = styled(Box, {
  shouldForwardProp: (prop) => prop !== 'bgColor' && prop !== 'textColor',
})<{ bgColor: string; textColor: string }>(({ bgColor, textColor }) => ({
  position: 'absolute',
  top: 12,
  left: 12,
  padding: '6px 12px',
  fontSize: '0.75rem',
  fontWeight: 500,
  borderRadius: 20,
  fontFamily: '"IBM Plex Sans", sans-serif',
  backgroundColor: bgColor,
  color: textColor,
}));

const RatingBadge = styled(Box)({
  position: 'absolute',
  top: 12,
  right: 12,
  display: 'flex',
  alignItems: 'center',
  gap: 4,
  padding: '6px 10px',
  borderRadius: 20,
  fontSize: '0.875rem',
  fontWeight: 500,
  fontFamily: '"IBM Plex Mono", monospace',
  backgroundColor: 'rgba(0, 0, 0, 0.7)',
  color: '#FFFFFF',
  backdropFilter: 'blur(8px)',
});

const ShareButton = styled(IconButton)(({ theme }) => ({
  position: 'absolute',
  bottom: 12,
  right: 12,
  backgroundColor: 'rgba(255, 255, 255, 0.9)',
  backdropFilter: 'blur(4px)',
  padding: 8,
  '&:hover': {
    backgroundColor: theme.palette.background.paper,
  },
}));

const ContentSection = styled(Box)({
  padding: 16,
});

const VenueName = styled(Typography)(({ theme }) => ({
  fontFamily: '"IBM Plex Mono", monospace',
  fontWeight: 600,
  fontSize: '1.125rem',
  color: theme.palette.text.primary,
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
  marginBottom: 4,
}));

const LocationRow = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: 4,
  marginTop: 4,
  color: theme.palette.text.secondary,
  fontSize: '0.875rem',
}));

const LocationText = styled(Typography)({
  fontFamily: '"IBM Plex Sans", sans-serif',
  fontSize: '0.875rem',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
});

const Description = styled(Typography)(({ theme }) => ({
  fontFamily: '"IBM Plex Sans", sans-serif',
  fontSize: '0.875rem',
  color: theme.palette.text.secondary,
  marginTop: 8,
  lineHeight: 1.5,
  display: '-webkit-box',
  WebkitLineClamp: 2,
  WebkitBoxOrient: 'vertical',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
}));

const Footer = styled(Box)({
  marginTop: 16,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
});

const PriceContainer = styled(Box)({
  display: 'flex',
  alignItems: 'baseline',
  gap: 4,
});

const Price = styled(Typography)(({ theme }) => ({
  fontFamily: '"IBM Plex Mono", monospace',
  fontSize: '1.25rem',
  fontWeight: 700,
  color: theme.palette.success.main,
}));

const PriceUnit = styled(Typography)(({ theme }) => ({
  fontFamily: '"IBM Plex Sans", sans-serif',
  fontSize: '0.875rem',
  color: theme.palette.text.secondary,
}));

const ReviewCount = styled(Typography)(({ theme }) => ({
  fontFamily: '"IBM Plex Sans", sans-serif',
  fontSize: '0.875rem',
  color: theme.palette.text.secondary,
}));

export const VenueCard = memo(function VenueCard({ venue, onClick }: VenueCardProps) {
  const { data: venueTypes } = useVenueTypes();
  const [imageError, setImageError] = useState(false);

  const formatPrice = (price: number) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: venue.currency || 'INR',
      maximumFractionDigits: 0,
    }).format(price);
  };

  const handleShare = (e: React.MouseEvent) => {
    e.stopPropagation(); // Prevent card click
    const shareUrl = venue.seoUrl
      ? `${window.location.origin}${venue.seoUrl}`
      : `${window.location.origin}/venues/${venue.slug}`;
    const shareText = `Check out ${venue.name} - ${venue.city?.name || ''}`;

    if (navigator.share) {
      navigator
        .share({
          title: venue.name,
          text: shareText,
          url: shareUrl,
        })
        .catch(() => {
          // User cancelled - fall back to WhatsApp
          const whatsappUrl = `https://wa.me/?text=${encodeURIComponent(`${shareText}\n\n${shareUrl}`)}`;
          window.open(whatsappUrl, '_blank');
        });
    } else {
      // Desktop - open WhatsApp directly
      const whatsappUrl = `https://wa.me/?text=${encodeURIComponent(`${shareText}\n\n${shareUrl}`)}`;
      window.open(whatsappUrl, '_blank');
    }
  };

  const venueTypeLabel = getVenueTypeLabel(venue.venueType, venueTypes);
  const venueTypeColors = getVenueTypeColor(venue.venueType, venueTypes);

  // Use fallback image if coverImage is missing (matching Mobile behavior)
  const imageSrc = venue.coverImage
    ? venue.coverImage
    : `https://picsum.photos/seed/${venue.id}/400/200`;

  const showImage = !imageError;

  return (
    <CardContainer onClick={onClick} data-testid="venue-card">
      {/* Cover Image */}
      <ImageSection>
        {showImage ? (
          <CoverImage
            src={imageSrc}
            alt={venue.name}
            loading="lazy"
            onError={() => setImageError(true)}
          />
        ) : (
          <ImagePlaceholder>
            <ImageOutlinedIcon sx={{ fontSize: 64 }} />
          </ImagePlaceholder>
        )}

        {/* Type Badge */}
        <VenueTypeBadge bgColor={venueTypeColors.bg} textColor={venueTypeColors.text}>
          {venueTypeLabel}
        </VenueTypeBadge>

        {/* Rating Badge */}
        {venue.averageRating && (
          <RatingBadge>
            <StarIcon sx={{ fontSize: 16, color: '#FBBF24' }} />
            <span>{venue.averageRating.toFixed(1)}</span>
          </RatingBadge>
        )}

        {/* Share Button */}
        <ShareButton onClick={handleShare} aria-label="Share venue" size="small">
          <ShareIcon sx={{ fontSize: 18 }} />
        </ShareButton>
      </ImageSection>

      {/* Content */}
      <ContentSection>
        <VenueName>{venue.name}</VenueName>

        {/* Location */}
        <LocationRow>
          <LocationOnOutlinedIcon sx={{ fontSize: 16 }} />
          <LocationText>
            {venue.locality ? `${venue.locality}, ` : ''}
            {venue.city?.name || 'Unknown City'}
          </LocationText>
        </LocationRow>

        {/* Description */}
        <Description>{venue.description}</Description>

        {/* Footer */}
        <Footer>
          {venue.minPrice !== null && venue.minPrice !== undefined && (
            <PriceContainer>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.25 }}>
                <PriceUnit sx={{ fontSize: '0.75rem', lineHeight: 1 }}>Starting from</PriceUnit>
                <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 0.5 }}>
                  <Price>{formatPrice(venue.minPrice)}</Price>
                  <PriceUnit>/hr</PriceUnit>
                </Box>
              </Box>
            </PriceContainer>
          )}

          {venue.totalReviews > 0 && (
            <ReviewCount>
              {venue.totalReviews} review{venue.totalReviews !== 1 ? 's' : ''}
            </ReviewCount>
          )}
        </Footer>
      </ContentSection>
    </CardContainer>
  );
});
